import React from 'react';
import styled from 'styled-components';
import {
  IonIcon,
  IonBadge,
  IonButton,
  IonProgressBar,
  IonSpinner,
} from '@ionic/react';
import {
  checkmarkCircle,
  closeCircle,
  close,
  refreshOutline,
  trashOutline,
  cloudUploadOutline,
  timeOutline,
  alertCircleOutline,
} from 'ionicons/icons';
import { ImageStatus } from '../../../services/buildfire/rdb/cases';
import useThumbnailUrl from '../../../hooks/useThumbnail';
import useObjectUrl from '../../../hooks/useObjectUrl';
import useRemotePreviewUrl from '../../../hooks/useRemotePreviewUrl';
import { useFirebase } from '../../../services/firebase';

interface ImageStatusCardProps {
  image: File | ImageStatus | string;
  onRetry?: () => void;
  onRemove?: () => void;
  progress?: number;
  disabled?: boolean;
  userId?: string;
}

const Card = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 16px;
  overflow: visible;
  background: #f2f2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.06);

  @media (min-width: 768px) {
    border-radius: 12px;
  }
`;

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    border-radius: 12px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StatusOverlay = styled.div<{ $status: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(props) => {
    switch (props.$status) {
      case 'failed':
        return 'linear-gradient(135deg, rgba(255, 89, 88, 0.65) 0%, rgba(255, 105, 120, 0.60) 100%)';
      case 'uploading':
        return 'rgba(0, 0, 0, 0.5)';
      default:
        return 'transparent';
    }
  }};
  backdrop-filter: ${(props) =>
    props.$status === 'failed' || props.$status === 'uploading'
      ? 'blur(12px)'
      : 'none'};
  -webkit-backdrop-filter: ${(props) =>
    props.$status === 'failed' || props.$status === 'uploading'
      ? 'blur(12px)'
      : 'none'};
  pointer-events: ${(props) => (props.$status === 'failed' ? 'auto' : 'none')};
  border-radius: 16px;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    border-radius: 12px;
  }
`;

const StatusBadge = styled(IonBadge)<{ $status: string }>`
  position: absolute;
  top: 6px;
  left: 6px;
  right: auto;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  padding: 3px 7px;
  border-radius: 100px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  --background: ${(props) => {
    switch (props.$status) {
      case 'uploaded':
        return '#34c759';
      case 'uploading':
        return '#007aff';
      case 'failed':
        return '#ef4444';
      case 'pending':
      default:
        return '#8e8e93';
    }
  }};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

  @media (min-width: 480px) {
    font-size: 10px;
    padding: 4px 8px;
    gap: 4px;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 10;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
    transform: scale(1.1);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  }

  &:active {
    background: rgba(0, 0, 0, 0.85);
    transform: scale(1.05);
  }

  ion-icon {
    font-size: 20px;
    color: white;
  }

  @media (min-width: 768px) {
    width: 28px;
    height: 28px;
    top: -6px;
    right: -6px;

    ion-icon {
      font-size: 18px;
    }
  }
`;

const RetryButton = styled(IonButton)`
  --padding-start: 18px;
  --padding-end: 18px;
  height: 44px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 22px;
  --border-radius: 22px;
  margin-top: 12px;
  text-transform: none;
  --background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.85) 100%
  );
  --background-activated: linear-gradient(
    135deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.95) 100%
  );
  --background-hover: linear-gradient(
    135deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.95) 100%
  );
  --color: #ff3b30;
  --box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.2s ease;
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;

  &::part(native) {
    border-radius: 22px;
    overflow: hidden;
  }

  &:hover {
    --box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    --box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  }

  &[disabled] {
    --background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.7) 0%,
      rgba(255, 255, 255, 0.6) 100%
    );
    --color: rgba(255, 59, 48, 0.5);
    opacity: 1;
  }

  ion-icon {
    font-size: 18px;
  }

  @media (min-width: 480px) and (max-width: 767px) {
    --padding-start: 16px;
    --padding-end: 16px;
    font-size: 13px;

    ion-icon {
      font-size: 16px;
    }
  }
`;

const ProgressContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.75);
  padding: 8px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const getImageName = (image: File | ImageStatus | string) => {
  if (image instanceof File) {
    return image.name;
  }
  if (typeof image === 'string') {
    return image;
  }
  return image.filename;
};

const ImageStatusCard: React.FC<ImageStatusCardProps> = ({
  image,
  onRetry,
  onRemove,
  progress,
  disabled,
  userId,
}) => {
  const { currentUser } = useFirebase();
  const isFile = image instanceof File;
  const isString = typeof image === 'string';
  const isImageFile =
    isFile && typeof (image as File).type === 'string'
      ? (image as File).type.startsWith('image/')
      : false;
  const imageStatus = !isFile && !isString ? (image as ImageStatus) : null;
  const status = isFile
    ? 'pending'
    : isString
    ? 'uploaded'
    : imageStatus!.status;

  const { url: thumbnailUrl, status: thumbnailStatus } = useThumbnailUrl(
    isFile && isImageFile ? (image as File) : undefined
  );
  const fallbackObjectUrl = useObjectUrl(
    isFile && (!isImageFile || thumbnailStatus === 'error')
      ? (image as File)
      : undefined
  );

  const remoteFilename = React.useMemo(() => {
    if (isString) {
      return image as string;
    }
    if (imageStatus && imageStatus.filename) {
      return imageStatus.filename;
    }
    return undefined;
  }, [isString, image, imageStatus]);

  const uid = userId || currentUser?.uid;
  const { url: remotePreviewUrl, status: remotePreviewStatus } = useRemotePreviewUrl(remoteFilename, uid);

  const isLoadingRemote = (isString || imageStatus) && remotePreviewStatus === 'loading';

  const displayUrl = React.useMemo(() => {
    if (isFile) {
      if (isImageFile) {
        return thumbnailUrl || fallbackObjectUrl;
      }
      return fallbackObjectUrl;
    }
    if (isString || imageStatus) {
      return remotePreviewUrl;
    }
    return undefined;
  }, [isFile, isImageFile, isString, imageStatus, thumbnailUrl, fallbackObjectUrl, remotePreviewUrl]);

  const statusIcon = React.useMemo(() => {
    switch (status) {
      case 'uploaded':
        return checkmarkCircle;
      case 'uploading':
        return cloudUploadOutline;
      case 'failed':
        return closeCircle;
      case 'pending':
      default:
        return timeOutline;
    }
  }, [status]);

  const statusLabel = React.useMemo(() => {
    switch (status) {
      case 'uploaded':
        return 'Uploaded';
      case 'uploading':
        return 'Uploading';
      case 'failed':
        return 'Failed';
      case 'pending':
      default:
        return 'Pending';
    }
  }, [status]);

  return (
    <Card title={getImageName(image)}>
      <ImageWrapper>
        {displayUrl && typeof displayUrl === 'string' ? (
          <Image src={displayUrl as string} alt="Upload preview" />
        ) : (
          <IonIcon
            icon={cloudUploadOutline}
            style={{ fontSize: '48px', color: '#ccc' }}
          />
        )}
      </ImageWrapper>

      {!isFile && (
        <StatusBadge $status={status}>
          <IonIcon icon={statusIcon} style={{ fontSize: '12px' }} />
          {statusLabel}
        </StatusBadge>
      )}

      {status === 'uploading' && (
        <StatusOverlay $status={status}>
          <IonSpinner name="crescent" color="light" />
          {typeof progress === 'number' && (
            <ProgressContainer>
              <IonProgressBar value={progress} color="light" />
            </ProgressContainer>
          )}
        </StatusOverlay>
      )}

      {isLoadingRemote && (
        <StatusOverlay $status="uploading">
          <IonSpinner name="crescent" color="light" />
        </StatusOverlay>
      )}

      {status === 'failed' && !isFile && !isString && (
        <StatusOverlay $status={status}>
          <IonIcon
            icon={alertCircleOutline}
            style={{
              fontSize: '36px',
              color: 'rgba(255, 255, 255, 0.9)',
              filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.25))',
            }}
          />
          {onRetry && (
            <RetryButton
              title="Retry upload"
              size="default"
              fill="clear"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              {disabled ? (
                <>
                  <IonSpinner name="crescent" style={{ marginRight: '6px' }} />
                  Retrying...
                </>
              ) : (
                <>
                  <IonIcon icon={refreshOutline} slot="start" />
                  Retry
                </>
              )}
            </RetryButton>
          )}
        </StatusOverlay>
      )}

      {onRemove && (
        <RemoveButton
          type="button"
          title="Remove image"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <IonIcon icon={close} />
        </RemoveButton>
      )}
    </Card>
  );
};

export default ImageStatusCard;
